import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Clock, Users, Palette, Check, Info } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { isProgramPublic } from '../utils/programVisibility';
import { mediaUrl, resolveImage } from '../utils/mediaUrl';

const API_URL = "";

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// The day tag comes from the session's own schedule/day fields — never guessed.
const getDayTag = (session) => {
  const haystack = `${session.daysOfWeek || ''} ${session.schedule || ''} ${session.time || ''} ${session.title || ''}`;
  const days = DAY_NAMES.filter(d => haystack.toLowerCase().includes(d.toLowerCase()));
  if (days.length === 0) return null;
  return days.map(d => `${d}s`).join(' & ');
};

const getTypeTag = (session) => {
  const custom = (session.session_type_label || '').trim();
  if (custom) return custom;
  const title = session.title || '';
  if (title.includes('Long')) return 'Long Pose';
  if (title.includes('Multiple')) return 'Quick Poses';
  if (title.includes('Creative')) return 'Costumed Model';
  if (title.includes('Third Space')) return 'Open Studio';
  if (/figure|life drawing|nude/i.test(title)) return 'Figure';
  if (/portrait/i.test(title)) return 'Portrait';
  return session.category || 'Open Session';
};

const getSessionImage = (session) =>
  resolveImage(session, 'image_ref', 'images', 'classImage', 'instructorImage');

export const OpenSessions = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const res = await fetch(`${API_URL}/api/content/programs`);
        if (res.ok) {
          const programs = await res.json();
          // Filter for PayPal-enabled sessions (Open Sessions)
          const openSessions = programs.filter(p => p.isPayPal === true && isProgramPublic(p));
          setSessions(openSessions);
        }
      } catch (err) {
        console.error('Error fetching sessions:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSessions();
  }, []);

  const sessionDays = (() => {
    const weekOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const found = weekOrder.filter(d =>
      sessions.some(s => (getDayTag(s) || '').includes(d))
    ).map(d => `${d}s`);
    if (found.length === 0) return '';
    if (found.length === 1) return found[0];
    return `${found.slice(0, -1).join(', ')} & ${found[found.length - 1]}`;
  })();

  const whatToBring = [
    "Your own drawing materials (charcoal, pencils, pastels, etc.)",
    "Sketchbook or drawing pad",
    "Erasers and blending tools",
    "Portable easel (optional - some available on site)"
  ];

  const sessionBenefits = [
    { icon: Users, title: "All Skill Levels", desc: "Beginners to advanced artists welcome" },
    { icon: Palette, title: "Live Models", desc: "Professional nude models provided" },
    { icon: Clock, title: "Flexible Format", desc: "Non-instructed, work at your own pace" },
    { icon: Info, title: "Equipment Available", desc: "Easels and tables provided" }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-stone-600">Loading sessions...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="open-sessions-page">
      <SEOHead
        title="Open Studio Sessions — Life Drawing & More"
        description="Drop-in open studio sessions at The Coppini Academy of Fine Art in San Antonio. Life drawing, long-pose, portrait sessions, and creative live model sessions for all levels."
        canonicalPath="/classes/open-sessions"
      />
      {/* Hero */}
      <section 
        className="relative bg-stone-900 text-white py-20"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(28, 25, 23, 0.85), rgba(28, 25, 23, 0.9)), url('https://customer-assets.emergentagent.com/job_fine-art-academy-2/artifacts/mdg2ngt1_12_1440.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-serif mb-6">Open Sessions</h1>
          <p className="text-xl text-stone-300 mb-4">
            Non-instructed life drawing sessions with professional models
          </p>
          <p className="text-stone-400" data-testid="open-sessions-hero-days">
            {sessionDays ? `Sessions on ${sessionDays} — see times below` : 'See below for session days and times'}
          </p>
          <div className="flex justify-center gap-4 mt-8">
            <div className="bg-white/10 px-4 py-2 rounded-lg">
              <p className="text-cafa-blue-300 font-bold">$10</p>
              <p className="text-xs text-stone-400">Members</p>
            </div>
            <div className="bg-white/10 px-4 py-2 rounded-lg">
              <p className="text-white font-bold">$25</p>
              <p className="text-xs text-stone-400">Non-Members</p>
            </div>
          </div>
        </div>
      </section>

      {/* What Are Open Sessions */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-6 text-center">What Are Open Sessions?</h2>
          <p className="text-lg text-stone-700 leading-relaxed text-center mb-12">
            Open Sessions provide artists of all skill levels the opportunity to practice figure drawing 
            from live models in a relaxed, non-instructed environment. Whether you're a beginner looking 
            to develop your skills or an experienced artist wanting to keep your hand sharp, these sessions 
            offer the perfect space to work at your own pace.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {sessionBenefits.map((benefit, index) => (
              <div key={index} className="text-center p-6 bg-stone-50 rounded-lg">
                <benefit.icon className="w-10 h-10 text-cafa-red mx-auto mb-4" />
                <h3 className="font-semibold text-stone-900 mb-2">{benefit.title}</h3>
                <p className="text-sm text-stone-600">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Session Types */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-4 text-center">Session Types</h2>
          <p className="text-stone-600 text-center mb-12 max-w-2xl mx-auto">
            Choose the session format that best suits your practice needs. Life drawing sessions 
            run Wednesday and Thursday evenings, and portrait painting is Friday mornings.
          </p>
          
          {sessions.length === 0 ? (
            <div className="text-center text-stone-600 py-12">
              <p>No open sessions are currently scheduled. Please check back later!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {sessions.map((session) => (
                <Card key={session.id} className="border-stone-200 hover:shadow-lg transition-shadow overflow-hidden" data-testid={`session-card-${session.id}`}>
                  {/* Session Image */}
                  {getSessionImage(session) && (
                    <div className="h-56 overflow-hidden bg-stone-100">
                      <img 
                        src={mediaUrl(getSessionImage(session), 'card')} 
                        alt={session.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                        data-testid={`session-image-${session.id}`}
                      />
                    </div>
                  )}
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <span className="px-3 py-1 bg-cafa-red-100 text-cafa-red-900 text-xs font-medium rounded-full" data-testid={`session-type-tag-${session.id}`}>
                        {getTypeTag(session)}
                      </span>
                      {getDayTag(session) && (
                        <span className="px-3 py-1 bg-stone-100 text-stone-600 text-xs font-medium rounded-full" data-testid={`session-day-tag-${session.id}`}>
                          {getDayTag(session)}
                        </span>
                      )}
                      {session.schedule?.includes('Monthly') && (
                        <span className="px-3 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded-full">
                          Monthly
                        </span>
                      )}
                    </div>
                    
                    <h3 className="text-xl font-semibold text-stone-900 mb-3">{session.title}</h3>
                    <p className="text-sm text-stone-600 mb-4">{session.description}</p>
                    
                    <div className="space-y-2 text-sm text-stone-500 mb-4">
                      <p><strong className="text-stone-700">Time:</strong> {session.schedule}</p>
                      {session.duration && (
                        <p><strong className="text-stone-700">Duration:</strong> {session.duration}</p>
                      )}
                      {(session.session_format_note || '').trim() ? (
                        <p><strong className="text-stone-700">Format:</strong> {session.session_format_note.trim()}</p>
                      ) : session.title.includes('Long') ? (
                        <p><strong className="text-stone-700">Pose Length:</strong> 1 pose per session with 10-minute breaks</p>
                      ) : session.title.includes('Multiple') ? (
                        <p><strong className="text-stone-700">Pose Length:</strong> 1, 3, 5, and 10-minute poses</p>
                      ) : session.title.includes('Creative') ? (
                        <p><strong className="text-stone-700">Format:</strong> Themed costume model sessions</p>
                      ) : session.title.includes('Third Space') || session.category === 'Open Studio' ? (
                        <p><strong className="text-stone-700">Format:</strong> Independent work</p>
                      ) : session.title.includes('Portrait') ? (
                        <p><strong className="text-stone-700">Format:</strong> Model provided for painting</p>
                      ) : null}
                    </div>
                    
                    <div className="pt-4 border-t border-stone-200">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="text-sm text-stone-600">Members: <span className="font-bold text-cafa-red">{session.memberPrice === 0 ? 'FREE' : `$${session.memberPrice}`}</span></p>
                          <p className="text-sm text-stone-600">Non-Members: <span className="font-bold text-stone-700">${session.nonMemberPrice}</span></p>
                        </div>
                      </div>
                      <Button 
                        asChild
                        className="w-full bg-cafa-red hover:bg-cafa-red-900 text-white"
                        data-testid={`register-btn-${session.id}`}
                      >
                        <Link to={`/register-open-session?session=${session.id}`}>
                          Register Now
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* What to Bring */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-serif text-stone-900 mb-6">What to Bring</h2>
              <p className="text-stone-600 mb-6">
                Open Sessions are non-instructed, so bring your preferred drawing materials. 
                The Academy provides easels, tables, and the model—you bring your creativity!
              </p>
              <ul className="space-y-3">
                {whatToBring.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-cafa-red mt-0.5 flex-shrink-0" />
                    <span className="text-stone-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-cafa-red-50 p-8 rounded-xl">
              <h3 className="text-xl font-semibold text-stone-900 mb-4">Session Schedule</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-cafa-blue-200">
                  <span className="text-stone-700">Days</span>
                  <span className="font-medium text-stone-900 text-right" data-testid="session-schedule-days">{sessionDays || 'See sessions above'}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-cafa-blue-200">
                  <span className="text-stone-700">Time</span>
                  <span className="font-medium text-stone-900">Varies by session</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-cafa-blue-200">
                  <span className="text-stone-700">Duration</span>
                  <span className="font-medium text-stone-900">2.5 – 3 Hours</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-stone-700">Closed</span>
                  <span className="font-medium text-stone-900">Major Holidays</span>
                </div>
              </div>
              <p className="text-sm text-stone-500 mt-4">
                Check the <Link to="/calendar" className="text-cafa-red hover:underline">Calendar</Link> for specific dates and availability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-stone-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-serif mb-4">Save with Membership</h2>
          <p className="text-stone-300 mb-8">
            Members pay just $10 per session—less than half the non-member price. 
            If you attend regularly, membership pays for itself quickly!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="bg-cafa-red hover:bg-cafa-red-700 text-white">
              <Link to="/membership">Become a Member</Link>
            </Button>
            <Button asChild variant="outline" className="border-white text-white hover:bg-white/10">
              <Link to="/calendar">View Calendar</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
